import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import PortalLayout from "../components/PortalLayout";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import {
  Upload, Loader2, Building2, Heart, Trophy, GraduationCap,
  HeartHandshake, Briefcase, Droplets, Lock, Sparkles, Check,
  TrafficCone,
} from "lucide-react";

const INDUSTRY_ICONS = {
  building: Building2, heart: Heart, trophy: Trophy,
  "graduation-cap": GraduationCap, "heart-handshake": HeartHandshake,
  briefcase: Briefcase, droplets: Droplets, "traffic-cone": TrafficCone,
};

const AuditUpload = () => {
  const [orgs, setOrgs] = useState([]);
  const [orgId, setOrgId] = useState("");
  const [industries, setIndustries] = useState([]);
  const [industry, setIndustry] = useState("municipality");
  const [templates, setTemplates] = useState([]);
  const [templateKey, setTemplateKey] = useState("municipal_finance_template");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [detection, setDetection] = useState(null); // { detected, confidence, display_name, ... }
  const [detecting, setDetecting] = useState(false);
  const [perms, setPerms] = useState(null);
  const [demoDatasets, setDemoDatasets] = useState([]);
  const [demoKey, setDemoKey] = useState("");
  const detectAbort = useRef(null);
  const nav = useNavigate();

  const isSalesDemo = perms?.role === "sales_demo";
  const demoMode = !!perms?.demo_mode;
  const showDemoDropdown = isSalesDemo || demoMode;

  // Load orgs + industries + permissions on mount
  useEffect(() => {
    api.get("/portal/organizations").then(r => {
      const list = r.data?.organizations || r.data?.orgs || [];
      setOrgs(list);
      const stored = typeof window !== "undefined" && localStorage.getItem("auditiq_active_org");
      if (stored && list.find(o => o.id === stored)) setOrgId(stored);
      else if (list[0]) setOrgId(list[0].id);
    }).catch(() => {});
    api.get("/auditiq/industries").then(r => {
      setIndustries(r.data?.industries || []);
    }).catch(() => {});
    api.get("/auditiq/my-permissions").then(r => setPerms(r.data)).catch(() => {});
    api.get("/auditiq/demo-datasets").then(r => setDemoDatasets(r.data?.datasets || [])).catch(() => {});
  }, []);

  const onDemoDatasetChange = (key) => {
    setDemoKey(key);
    if (!key) return;
    const ds = demoDatasets.find(d => d.key === key);
    if (ds) {
      if (ds.industry) setIndustry(ds.industry);
      if (ds.template) setTemplateKey(ds.template);
    }
  };

  // Load templates filtered by industry whenever industry changes
  useEffect(() => {
    if (!industry) return;
    api.get(`/auditiq/templates?org_type=${encodeURIComponent(industry)}`).then(r => {
      const list = r.data?.templates || [];
      setTemplates(list);
      if (list.length && !list.find(t => t.key === templateKey)) {
        setTemplateKey(list[0].key);
      }
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [industry]);

  const onFileChosen = async (f) => {
    setFile(f);
    setDetection(null);
    if (!f || !f.name.toLowerCase().endsWith(".csv")) return;
    setDetecting(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const r = await api.post("/auditiq/detect-template", fd, {
        headers: { "Content-Type": "multipart/form-data" }, timeout: 30000,
      });
      setDetection(r.data);
    } catch {
      // Silent — manual selection still works
    }
    setDetecting(false);
  };

  const useRecommended = () => {
    if (!detection?.detected) return;
    setTemplateKey(detection.detected);
    toast.success(`Template set: ${detection.display_name}`);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!orgId)        return toast.error("Select an organization");

    // Demo dataset flow — seed instead of real upload
    if (demoKey && (isSalesDemo || !file)) {
      setBusy(true);
      try {
        const r = await api.post(`/auditiq/demo-datasets/${demoKey}/seed`);
        toast.success(`Demo dataset seeded — ${r.data.dataset?.label || demoKey}`);
        nav(`/auditiq/dashboard?audit_id=${r.data.audit_id}`);
      } catch (err) {
        toast.error(err?.response?.data?.detail || "Demo seed failed");
      }
      setBusy(false);
      return;
    }

    if (!file)         return toast.error("Choose a CSV file");
    if (!file.name.toLowerCase().endsWith(".csv"))
                       return toast.error("File must be a CSV");
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const qs = new URLSearchParams({
        organization_id: orgId,
        template_key: templateKey,
        industry: industry || "municipality",
      });
      const r = await api.post(
        `/auditiq/upload?${qs.toString()}`,
        fd,
        { headers: { "Content-Type": "multipart/form-data" }, timeout: 180000 },
      );
      toast.success(`Audit complete — ${r.data.findings_created} findings`);
      nav(`/auditiq/dashboard?audit_id=${r.data.audit_id}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    }
    setBusy(false);
  };

  const selectedIndustry = industries.find(i => i.key === industry);

  return (
    <PortalLayout>
      <div className="space-y-6">
        <div>
          <div className="text-[11px] uppercase tracking-[0.28em] text-electric">AuditIQ</div>
          <h1 className="font-display text-3xl md:text-4xl mt-2">Upload Audit Data</h1>
          <p className="text-sm text-cream-dim mt-1 max-w-3xl">
            Pick your industry, drop a CSV, and AuditIQ will auto-detect the best template,
            run the appropriate engines, and generate Chet's executive intelligence.
          </p>
        </div>

        {/* Demo dataset picker (sales_demo or global demo_mode) */}
        {showDemoDropdown && (
          <div className="glass p-5" data-testid="demo-dataset-picker">
            <div className="text-[11px] uppercase tracking-[0.22em] text-gold mb-2">
              {isSalesDemo ? "Sales accounts use demo datasets only" : "Demo dataset (optional)"}
            </div>
            <Select value={demoKey || "__none"} onValueChange={(v) => onDemoDatasetChange(v === "__none" ? "" : v)}>
              <SelectTrigger className="bg-navy-deep border-gold-dim/40 text-cream" data-testid="demo-dataset-select">
                <SelectValue placeholder="Select a demo dataset…" />
              </SelectTrigger>
              <SelectContent className="bg-navy-soft border-gold-dim/40 text-cream max-h-80">
                <SelectItem value="__none">— none —</SelectItem>
                {demoDatasets.map(d => (
                  <SelectItem key={d.key} value={d.key}>{d.label} · {d.industry}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {demoKey && (
              <div className="mt-2 text-[11px] text-cream-dim">
                {isSalesDemo
                  ? `Sales accounts use demo datasets only.`
                  : `Using demo dataset — upload a real file to override.`}
              </div>
            )}
          </div>
        )}

        {/* STEP 1 — Industry selector */}
        <div>
          <div className="text-[11px] uppercase tracking-[0.22em] text-electric mb-2">Step 1 · Industry</div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-7 gap-3" data-testid="industry-grid">
            {industries.map(ind => {
              const Icon = INDUSTRY_ICONS[ind.icon] || Building2;
              const active = ind.key === industry;
              return (
                <button
                  key={ind.key} type="button"
                  onClick={() => setIndustry(ind.key)}
                  data-testid={`industry-card-${ind.key}`}
                  className={`text-left glass p-3 rounded-md border-2 transition-colors ${
                    active ? "border-gold" : "border-transparent hover:border-electric/40"
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <Icon size={18} className={active ? "text-gold" : "text-electric"} />
                    {ind.privacy_mode && <Lock size={12} className="text-amber-300" title="Privacy mode" />}
                  </div>
                  <div className="text-sm font-medium text-cream mt-2">{ind.display_name}</div>
                  <div className="text-[11px] text-cream-dim mt-1 line-clamp-2">{ind.description}</div>
                  {ind.privacy_mode && (
                    <div className="text-[10px] text-amber-300 mt-2">Private — results not publicly shared.</div>
                  )}
                </button>
              );
            })}
          </div>
        </div>

        {/* STEP 2+3 form */}
        <form onSubmit={submit} className="glass p-6 grid lg:grid-cols-2 gap-5 max-w-3xl">
          <div>
            <label className="text-[11px] uppercase tracking-[0.22em] text-steel">Organization</label>
            <Select value={orgId} onValueChange={setOrgId}>
              <SelectTrigger className="mt-1 bg-navy-deep border-gold-dim/40 text-cream" data-testid="auditiq-org-select">
                <SelectValue placeholder="Select organization" />
              </SelectTrigger>
              <SelectContent className="bg-navy-soft border-gold-dim/40 text-cream">
                {orgs.map(o => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          <div>
            <label className="text-[11px] uppercase tracking-[0.22em] text-steel">Template</label>
            <Select value={templateKey} onValueChange={setTemplateKey}>
              <SelectTrigger className="mt-1 bg-navy-deep border-gold-dim/40 text-cream" data-testid="auditiq-template-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-navy-soft border-gold-dim/40 text-cream">
                {templates.map(t => (
                  <SelectItem key={t.key} value={t.key}>
                    {t.recommended ? "★ " : ""}{t.display_name}{t.privacy_mode ? "  ·  Private" : ""}{t.recommended ? "  ·  Recommended" : ""}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {selectedIndustry?.privacy_mode && (
              <div className="text-[10px] text-amber-300 mt-2 flex items-center gap-1">
                <Lock size={10} /> Industry-wide privacy mode — outputs are not publicly shareable.
              </div>
            )}
          </div>

          <div className="lg:col-span-2">
            {!isSalesDemo ? (
              <>
                <label className="text-[11px] uppercase tracking-[0.22em] text-steel">CSV file</label>
                <Input type="file" accept=".csv,text/csv"
                  onChange={(e) => onFileChosen(e.target.files?.[0] || null)}
                  className="mt-1 bg-navy-deep border-gold-dim/40 text-cream"
                  data-testid="auditiq-file-input" />
              </>
            ) : (
              <div className="rounded-md border border-gold-dim/40 bg-navy-deep px-4 py-3 text-xs text-cream-dim" data-testid="sales-demo-upload-note">
                File upload disabled for Sales accounts. Select a demo dataset above to
                generate a sample audit.
              </div>
            )}

            {/* Auto-detect recommendation banner */}
            {detecting && (
              <div className="mt-3 text-[12px] text-cream-dim flex items-center gap-2" data-testid="detect-pending">
                <Loader2 size={12} className="animate-spin" /> Auto-detecting template…
              </div>
            )}
            {detection && !detecting && detection.auto_detected && detection.detected !== templateKey && (
              <div className="mt-3 glass p-3 rounded-md flex items-center justify-between gap-3 flex-wrap"
                   data-testid="detect-recommendation">
                <div className="text-sm">
                  <Sparkles size={12} className="text-electric inline mr-1" />
                  Recommended template: <span className="text-electric">{detection.display_name}</span>
                  <span className="text-cream-dim ml-2 text-xs">
                    ({Math.round((detection.confidence || 0) * 100)}% header match)
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <Button type="button" onClick={useRecommended} className="btn-gold h-8 px-3 text-xs"
                          data-testid="detect-use-btn">
                    <Check size={12} className="mr-1" /> Use Recommended
                  </Button>
                </div>
              </div>
            )}
            {detection && !detecting && !detection.auto_detected && (
              <div className="mt-3 text-[12px] text-amber-300" data-testid="detect-low-confidence">
                Could not auto-detect template — please select manually.
              </div>
            )}
            {detection && !detecting && detection.detected === templateKey && (
              <div className="mt-3 text-[12px] text-emerald-300 flex items-center gap-1" data-testid="detect-match">
                <Check size={12} /> Template matches your file headers ({Math.round((detection.confidence || 0) * 100)}%).
              </div>
            )}
          </div>

          <div className="lg:col-span-2 flex justify-end">
            <Button type="submit" disabled={busy} className="btn-gold h-11 px-6" data-testid="auditiq-upload-submit">
              {busy ? <><Loader2 size={14} className="mr-2 animate-spin" /> Running engine…</>
                    : <><Upload size={14} className="mr-2" /> Run Audit</>}
            </Button>
          </div>
        </form>
      </div>
    </PortalLayout>
  );
};

export default AuditUpload;
